import { templateSchemasId, type TemplateSchemasId } from "./TemplateSchemas";
import { usersId, type UsersId } from "./Users";
import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for template_contents */
export type TemplateContentsId = string & { __flavor: "TemplateContentsId" };

/**
 * Represents the table public.template_contents
 * @uniqueConstraints {"template_contents_pkey":{"name":"template_contents_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"unique_published_versions":{"name":"unique_published_versions","isPrimary":false,"isUnique":true,"columns":[{"name":"template_schema_id","definition":"template_schema_id","predicate":null},{"name":"version_minor","definition":"version_minor","predicate":null},{"name":"version_patch","definition":"version_patch","predicate":null}]}}
 */
export default interface TemplateContentsTable {
  id: ColumnType<TemplateContentsId, TemplateContentsId | undefined, TemplateContentsId>;

  templateSchemaId: ColumnType<TemplateSchemasId, TemplateSchemasId, TemplateSchemasId>;

  versionMinor: ColumnType<number | null, number | null, number | null>;

  versionPatch: ColumnType<number | null, number | null, number | null>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  creatorId: ColumnType<UsersId | null, UsersId | null, UsersId | null>;
}

export type TemplateContents = Selectable<TemplateContentsTable>;

export type NewTemplateContents = Insertable<TemplateContentsTable>;

export type TemplateContentsUpdate = Updateable<TemplateContentsTable>;

export const templateContentsId = z.string() as unknown as z.Schema<TemplateContentsId>;

export const templateContents = z.object({
  id: templateContentsId,
  templateSchemaId: templateSchemasId,
  versionMinor: z.number().nullable(),
  versionPatch: z.number().nullable(),
  createdAt: z.date(),
  creatorId: usersId.nullable(),
});

export const templateContentsInitializer = z.object({
  id: templateContentsId.optional(),
  templateSchemaId: templateSchemasId,
  versionMinor: z.number().optional().nullable(),
  versionPatch: z.number().optional().nullable(),
  createdAt: z.date().optional(),
  creatorId: usersId.optional().nullable(),
});

export const templateContentsMutator = z.object({
  id: templateContentsId.optional(),
  templateSchemaId: templateSchemasId.optional(),
  versionMinor: z.number().optional().nullable(),
  versionPatch: z.number().optional().nullable(),
  createdAt: z.date().optional(),
  creatorId: usersId.optional().nullable(),
});

// ── constraints for TemplateContents ─────────────────────────────────
export const TemplateContentsConstraints = {
  template_contents_pkey: {
    name: "template_contents_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  unique_published_versions: {
    name: "unique_published_versions",
    isPrimary: false,
    isUnique: true,
    columns: [
      { name: "template_schema_id", definition: "template_schema_id", predicate: null },
      { name: "version_minor", definition: "version_minor", predicate: null },
      { name: "version_patch", definition: "version_patch", predicate: null },
    ],
  },
} as const;
export type TemplateContentsConstraintsType = typeof TemplateContentsConstraints;
